import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
// This component manages the game shop, bank, and premium progression systems.
import { ArrowLeft, ShoppingBag, Coins, Zap, Ticket, Play, Sparkles, Check, Heart, Loader2, Info, HelpCircle, Award, Star, Crown, ChevronRight } from 'lucide-react';
import ChestIcon from './ChestIcon';
import FeatureIcon from './FeatureIcon';
import { UserData } from '../hooks/useUser';
import CurrencyIcon from './CurrencyIcon';
import { cn } from '../lib/utils';
import GameModal, { ModalType } from './GameModal';
import QuantityPickerModal from './QuantityPickerModal';
import { BRONZE_REWARDS, SILVER_REWARDS, GOLD_REWARDS, DIAMOND_REWARDS } from '../utils/rewardSystem';
import { audioEngine } from '../utils/audioEngine';

import { adManager } from '../utils/adManager';
import { purchaseManager } from '../utils/purchaseManager';
import GalleryPhoto from './GalleryPhoto';
import { AVATARS } from '../utils/avatars';
import AvatarFrame from './AvatarFrame';
import { MILESTONE_OFFERS, MilestoneOffer } from '../utils/milestoneOffers';
import PremiumModal from './PremiumModal';
import { useTranslation } from 'react-i18next';

interface ShopProps {
  user: UserData;
  onBack: () => void;
  onBuyBox: (tier: keyof UserData['rewardBoxes'], costType: 'coins' | 'gems' | 'ad', costAmount: number, amount?: number) => Promise<boolean>;
  onOpenBox: (tier: 'bronze' | 'silver' | 'gold' | 'diamond') => void;
  onBuyTicket: () => Promise<boolean>;
  onBuyStarTicket: () => Promise<boolean>;
  onAddTickets: (amount: number) => Promise<void>;
  onUpdateMission?: (id: string, amount?: number) => void;
  initialTab?: 'market' | 'bank';
  onBuyAvatar: (id: string) => Promise<boolean>;
  onGrantMilestoneOfferRewards: (id: string) => Promise<boolean>;
  onExpireMilestoneOffer: (id: string) => Promise<void>;
  onRefillHearts: () => Promise<boolean>;
  onIncrementAdCounter: (type: 'heart' | 'box' | 'star_practice') => Promise<void>;
  onSetPremiumStatus: (status: boolean) => Promise<void>;
}

const DROP_RATES = {
  bronze: [
    { label: '1-Star Anima', chance: '60%' },
    { label: '2-Star Anima', chance: '20%' },
    { label: '5,000 Coins', chance: '4%' },
    { label: '2,500 Coins', chance: '10%' },
    { label: '10 Hoshi', chance: '1%' },
    { label: '1x Heart Ticket', chance: '4.9%' },
    { label: '1x Star Ticket', chance: '0.1%' },
  ],
  silver: [
    { label: '10 Hoshi', chance: '35%' },
    { label: '1-Star Anima', chance: '25%' },
    { label: '25 Hoshi', chance: '15%' },
    { label: '2-Star Anima', chance: '12.5%' },
    { label: '50 Hoshi', chance: '5%' },
    { label: '1x Heart Ticket', chance: '5%' },
    { label: '5x Heart Tickets', chance: '1.5%' },
    { label: '3-Star Anima', chance: '0.5%' },
    { label: '1x Star Ticket', chance: '0.5%' },
  ],
  gold: [
    { label: '10,000 Coins', chance: '25%' },
    { label: '2-Star Anima', chance: '25%' },
    { label: '30,000 Coins', chance: '15%' },
    { label: '3-Star Anima', chance: '6%' },
    { label: '5x Heart Tickets', chance: '10%' },
    { label: '10x Heart Tickets', chance: '5%' },
    { label: '50,000 Coins', chance: '5%' },
    { label: '100,000 Coins', chance: '4%' },
    { label: '1x Star Ticket', chance: '4%' },
    { label: '250,000 Coins', chance: '1%' },
  ],
  diamond: [
    { label: '2-Star Anima', chance: '50%' },
    { label: '3-Star Anima', chance: '34.5%' },
    { label: '10x Heart Tickets', chance: '10%' },
    { label: '1x Star Ticket', chance: '4%' },
    { label: '100 Hoshi', chance: '1%' },
    { label: '5x Star Tickets', chance: '0.4%' },
    { label: '500 Hoshi', chance: '0.1%' },
  ]
};

const BOX_PRICES_INTERNAL = {
  Bronze: { Ad: 0 },
  Silver: { GC: 10000 },
  Gold: { CC: 20 },
  Diamond: { CC: 50 }
};

type Tab = 'market' | 'avatar' | 'bank' | 'special';

export default function Shop({ user, onBack, onBuyBox, onBuyTicket, onBuyStarTicket, onAddTickets, onUpdateMission, onOpenBox, initialTab, onBuyAvatar, onGrantMilestoneOfferRewards, onExpireMilestoneOffer, onRefillHearts, onIncrementAdCounter, onSetPremiumStatus }: ShopProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>(initialTab || 'market');
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [simulatingOffer, setSimulatingOffer] = useState<MilestoneOffer | null>(null);
  const [offerTimeLeft, setOfferTimeLeft] = useState<string>('');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const [modal, setModal] = useState<{ 
    open: boolean; 
    title: string; 
    message: string; 
    type: ModalType;
    onConfirm?: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
  }>({
    open: false,
    title: '',
    message: '',
    type: 'info'
  });

  const [showDropRates, setShowDropRates] = useState<'bronze' | 'silver' | 'gold' | 'diamond' | null>(null);
  const [buyingBox, setBuyingBox] = useState<'Bronze' | 'Silver' | 'Gold' | 'Diamond' | null>(null);

  useEffect(() => {
    if (!user.activeMilestoneOffer) {
      setOfferTimeLeft('');
      return;
    }

    const expiresAt = new Date(user.activeMilestoneOffer.expiresAt).getTime();
    
    const updateTime = () => {
      const now = Date.now();
      const remainingMs = expiresAt - now;
      if (remainingMs <= 0) {
        onExpireMilestoneOffer(user.activeMilestoneOffer!.id);
        setOfferTimeLeft('');
        clearInterval(timer);
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const pad = (n: number) => String(n).padStart(2, '0');
      setOfferTimeLeft(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [user.activeMilestoneOffer, onExpireMilestoneOffer]);

  const showAlert = (title: string, message: string, type: ModalType = 'info') => {
    setModal({ open: true, title, message, type, onConfirm: undefined });
  };

  const getHeartAdCooldown = () => {
    const counters = user.adCounters;
    if (!counters) return null;
    
    if (counters.heartAdsCount >= 4) return "LIMIT";
    
    const now = new Date();
    const lastAd = new Date(counters.lastHeartAdTime);
    const msSinceLast = now.getTime() - lastAd.getTime();
    const sixHours = 6 * 60 * 60 * 1000;
    
    if (msSinceLast < sixHours) {
      const remainingMs = sixHours - msSinceLast;
      const h = Math.floor(remainingMs / (1000 * 60 * 60));
      const m = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${h}:${String(m).padStart(2, '0')}`;
    }
    return null;
  };

  const getBoxAdCooldown = () => {
    const counters = user.adCounters;
    if (!counters) return null;
    
    const now = new Date();
    const lastAd = new Date(counters.lastBoxAdTime);
    const msSinceLast = now.getTime() - lastAd.getTime();
    const oneHour = 60 * 60 * 1000;
    
    if (msSinceLast < oneHour) {
      const remainingMs = oneHour - msSinceLast;
      const m = Math.floor(remainingMs / (1000 * 60));
      const s = Math.floor((remainingMs % (1000 * 60)) / 1000);
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return null;
  };

  const showNotEnoughGemsAlert = () => {
    audioEngine.playNotEnough();
    setModal({
      open: true,
      title: t('shop.alerts.not_enough_hoshi'),
      message: t('shop.alerts.not_enough_hoshi_msg'),
      type: 'confirm',
      confirmLabel: t('shop.alerts.yes'),
      cancelLabel: t('shop.alerts.no'),
      onConfirm: () => setActiveTab('bank')
    });
  };


  const handleWatchAd = async () => {
    if (isProcessing) return;
    
    setIsProcessing('ad');
    
    // Premium players bypass ads
    if (!user.isPremium) {
      const success = await adManager.showRewardedAd('heart_refill');
      if (!success) {
        setIsProcessing(null);
        return;
      }
    }
    
    await onAddTickets(1);
    await onIncrementAdCounter('heart');
    showAlert(t('shop.alerts.ticket_claimed'), t('shop.alerts.ticket_claimed_msg'), "success");
    setIsProcessing(null);
  };

  const handleBuyTicket = async () => {
    if (user.coins < 2000) {
      audioEngine.playNotEnough();
      showAlert(t('shop.alerts.not_enough_coins'), t('shop.alerts.not_enough_coins_msg'));
      return;
    }
    
    setIsProcessing('ticket');
    const success = await onBuyTicket();
    if (success) {
      audioEngine.playClaim();
      showAlert(t('shop.alerts.purchased'), t('shop.alerts.purchased_msg'), "success");
    } else {
      showAlert(t('shop.alerts.error'), t('shop.alerts.error_generic'));
    }
    setIsProcessing(null);
  };

  const handleBuyStarTicket = async () => {
    if (isProcessing) return;
    setIsProcessing('star_ticket');
    
    const success = await onBuyStarTicket();
    if (success) {
      audioEngine.playClaim();
      showAlert(t('shop.alerts.purchase_successful'), t('shop.alerts.star_ticket_success'), "success");
    } else {
      audioEngine.playNotEnough();
      showAlert(t('shop.alerts.not_enough_tickets'), t('shop.alerts.not_enough_tickets_msg'), "error");
    }
    setIsProcessing(null);
  };

  const handleRefillHearts = async () => {
    if (isProcessing) return;
    setIsProcessing('refill');
    
    const success = await onRefillHearts();
    if (success) {
      audioEngine.playClaim();
      showAlert(t('shop.alerts.refilled'), t('shop.alerts.refilled_msg'), "success");
    } else {
      audioEngine.playNotEnough();
      showNotEnoughGemsAlert();
    }
    setIsProcessing(null);
  };

  const handleBuyAvatar = async (id: string) => {
    if (isProcessing) return;
    setIsProcessing(id);
    
    const success = await onBuyAvatar(id);
    if (success) {
      if (onUpdateMission) {
        onUpdateMission('collector_of_identities', 1);
      }
      audioEngine.playClaim();
      showAlert(t('shop.alerts.avatar_unlocked'), t('shop.alerts.avatar_unlocked_msg'), "success");
    } else {
      audioEngine.playNotEnough();
      showAlert(t('shop.alerts.not_enough_coins'), t('shop.alerts.not_enough_coins_generic'), "error");
    }
    setIsProcessing(null);
  };

  const handleBuyMilestoneOffer = async (offer: MilestoneOffer) => {
    if (isProcessing) return;
    
    // For web/simulator: trigger simulated checkout modal
    if (!purchaseManager.isAndroid()) {
      setSimulatingOffer(offer);
      return;
    }
    
    setIsProcessing(offer.id);
    try {
      await purchaseManager.buyMilestoneOffer(offer.id);
    } catch (error) {
      console.error(error);
      showAlert(t('shop.alerts.error'), t('shop.alerts.purchase_error'), "error");
    } finally {
      setIsProcessing(null);
    }
  };

  const handleCompleteSimulatedPurchase = async () => {
    if (!simulatingOffer) return;
    
    const offerId = simulatingOffer.id;
    setIsProcessing(offerId);
    
    // Call grant logic (which is passed down from App.tsx via onGrantMilestoneOfferRewards)
    const success = await onGrantMilestoneOfferRewards(offerId);
    
    if (success) {
      setSimulatingOffer(null);
      audioEngine.playClaim();
      showAlert(t('shop.alerts.purchase_successful'), t('shop.alerts.simulator_success'), "success");
    } else {
      showAlert(t('shop.alerts.error'), t('shop.alerts.simulator_error'), "error");
    }
    setIsProcessing(null);
  };

  const handleBuyBox = async (tier: keyof UserData['rewardBoxes'], costType: 'coins' | 'gems', costAmount: number, amount: number = 1) => {
    setIsProcessing(tier);
    
    const totalCost = costAmount * amount;
    
    // Double check balance
    if (costType === 'coins' && user.coins < totalCost) {
      audioEngine.playNotEnough();
      showAlert(t('shop.alerts.not_enough_coins'), t('shop.alerts.not_enough_coins_generic'), "error");
      setIsProcessing(null);
      setBuyingBox(null);
      return;
    }
    if (costType === 'gems' && user.gems < totalCost) {
      audioEngine.playNotEnough();
      showNotEnoughGemsAlert();
      setIsProcessing(null);
      setBuyingBox(null);
      return;
    }

    // Call API
    const success = await onBuyBox(tier, costType, costAmount, amount);
    
    if (success) {
      audioEngine.playClaim();
      showAlert(t('shop.alerts.purchase_successful'), t('shop.alerts.box_success', { count: amount, tier: tier.toUpperCase() }), "success");
    } else {
      showAlert(t('shop.alerts.error'), t('shop.alerts.purchase_failed'), "error");
    }
    
    setIsProcessing(null);
    setBuyingBox(null);
  };

  const handleConfirmQuantity = async (paymentMethod: 'GC' | 'CC' | 'Ad' | 'Inventory', count: number) => {
    if (!buyingBox) return;
    const boxId = buyingBox.toLowerCase() as keyof UserData['rewardBoxes'];
    
    if (paymentMethod === 'Inventory') {
      onOpenBox(boxId as any);
      setBuyingBox(null);
      return;
    }

    if (paymentMethod === 'Ad') {
      if (isProcessing) return;

      setIsProcessing(boxId);
      if (!user.isPremium) {
        const success = await adManager.showRewardedAd('ad_box');
        if (!success) {
          setIsProcessing(null);
          setBuyingBox(null);
          return;
        }
      }

      setIsProcessing(boxId);
      await onBuyBox(boxId, 'ad', 0);
      // Increment ad counter for everyone to set the cooldown timestamp
      await onIncrementAdCounter('box');
      
      audioEngine.playClaim();
      showAlert(t('shop.alerts.reward_claimed'), t('shop.alerts.reward_claimed_msg'), "success");
      setIsProcessing(null);
      setBuyingBox(null);
      return;
    }

    const costType = paymentMethod === 'GC' ? 'coins' : 'gems';
    const costPer = (BOX_PRICES_INTERNAL as any)[buyingBox][paymentMethod];
    await handleBuyBox(boxId, costType, costPer, count);
  };

  const gemPackages = [
    { id: 'gems_pouch', amount: 20, price: '$0.99', tier: 'Pouch', color: 'bg-purple-50', gems: 1, bonus: t('shop.gem_packages.base_rate') },
    { id: 'gems_bag', amount: 90, price: '$3.99', tier: 'Bag', color: 'bg-purple-100', gems: 3, bonus: '+10 Free Hoshi' },
    { id: 'gems_box', amount: 190, price: '$7.99', tier: 'Box', color: 'bg-indigo-50', gems: 5, bonus: '+30 Free Hoshi' },
    { id: 'gems_vault', amount: 375, price: '$14.99', tier: 'Vault', color: 'bg-indigo-100', gems: 8, bonus: '+75 Free Hoshi' },
    { id: 'gems_chest', amount: 520, price: '$19.99', tier: 'Chest', color: 'bg-cyan-50', gems: 10, bonus: '+120 Free Hoshi' },
    { id: 'gems_hoard', amount: 1000, price: '$34.99', tier: 'Hoard', color: 'bg-blue-50', gems: 12, bonus: '+300 Free Hoshi' },
    { id: 'gems_galactic', amount: 2200, price: '$69.99', tier: 'Galactic', color: 'bg-white', gems: 18, bonus: '+800 Free Hoshi' },
  ];

  return (
    <div className="flex flex-col h-full night-sky-bg text-slate-900 relative overflow-hidden">
      {/* 1. The Sheen (Middle Layer) */}
      <div className="glass-sweep-overlay" />
      
      {/* 2. Backdrop Filter (Above Sheen) */}
      <div className="absolute inset-0 frosted-glass-surface pointer-events-none" />

      {/* 3. Content (Top Layer) */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header with Currency */}
        <div className="bg-white/80 backdrop-blur-md border-b border-white/20 px-6 py-6 flex flex-col gap-4 relative z-10 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              id="shop-close-button"
              onClick={() => {
                audioEngine.playSound('click');
                onBack();
              }}
              className="p-3 bg-white rounded-2xl toy-3d-button border-none"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>

            <h2 className="text-3xl font-num font-black uppercase tracking-tight leading-none text-slate-900">{t('shop.title')}</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-yellow-400/10 px-3 py-1.5 rounded-2xl border border-yellow-400/20">
              <CurrencyIcon type="coin" size="sm" />
              <span className="font-num text-sm font-black text-yellow-600">{user.coins?.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-purple-500/10 px-3 py-1.5 rounded-2xl border border-purple-500/20">
              <CurrencyIcon type="gem" size="sm" />
              <span className="font-num text-sm font-black text-purple-600">{user.gems?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2">
          <button 
            onClick={() => {
              audioEngine.playSound('click');
              setActiveTab('market');
              setSelectedCategory(null);
            }}
            className={cn(
              "flex-1 py-3 px-2 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-1.5 border-2",
              activeTab === 'market' 
                ? "bg-slate-900 text-white border-slate-900 shadow-md translate-y-[-1px]" 
                : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
            )}
          >
            <FeatureIcon type="shop" size="sm" className="w-4 h-4 shrink-0" />
            {t('shop.tabs.market')}
          </button>
          
          <button 
            onClick={() => {
              audioEngine.playSound('click');
              setActiveTab('avatar');
              setSelectedCategory(null);
            }}
            className={cn(
              "flex-1 py-3 px-2 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-1.5 border-2",
              activeTab === 'avatar' 
                ? "bg-slate-900 text-white border-slate-900 shadow-md translate-y-[-1px]" 
                : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
            )}
          >
            <span className="text-sm leading-none shrink-0">👤</span>
            {t('shop.tabs.avatar')}
          </button>

          <button 
            onClick={() => {
              audioEngine.playSound('click');
              setActiveTab('bank');
              setSelectedCategory(null);
            }}
            className={cn(
              "flex-1 py-3 px-2 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-1.5 border-2",
              activeTab === 'bank' 
                ? "bg-slate-900 text-white border-slate-900 shadow-md translate-y-[-1px]" 
                : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
            )}
          >
            <CurrencyIcon type="gem" size="sm" className="w-4 h-4 shrink-0" />
            {t('shop.tabs.bank')}
          </button>

          <button 
            onClick={() => {
              audioEngine.playSound('click');
              setActiveTab('special');
              setSelectedCategory(null);
            }}
            className={cn(
              "flex-1 py-3 px-2 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-1.5 border-2",
              activeTab === 'special' 
                ? "bg-slate-900 text-white border-slate-900 shadow-md translate-y-[-1px]" 
                : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
            )}
          >
            <FeatureIcon type="special" size="sm" className="w-5 h-5 shrink-0 -my-0.5" />
            {t('shop.tabs.special')}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6 pb-24 relative z-10 scrollbar-none">
        <AnimatePresence mode="wait">
          {activeTab === 'market' ? (
            <motion.div 
              key="market"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 gap-4"
            >

              <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-2">{t('shop.sections.consumables')}</h3>
              
              {/* Ad for heart */}
              <div className="bg-white p-5 rounded-[32px] border-2 border-slate-100 flex items-center gap-5 shadow-sm">
                <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center shrink-0 border-2 border-rose-100">
                  <CurrencyIcon type="ticket_heart" size="lg" />
                </div>
                <div className="flex-1">
                  <div className="font-black text-slate-900 uppercase whitespace-nowrap">{t('shop.heart_ticket.name')}</div>
                  <div className="text-[10px] text-rose-500 font-black uppercase tracking-widest mt-0.5">{t('shop.heart_ticket.owned', { count: user.heartTickets })}</div>
                  <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{t('shop.heart_ticket.limit')}</div>
                </div>
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    handleWatchAd();
                  }}
                  disabled={isProcessing === 'ad' || !!getHeartAdCooldown()}
                  className={cn(
                    "w-[120px] py-3 rounded-2xl font-num text-lg uppercase toy-3d-button border-none shrink-0 flex items-center justify-center",
                    !!getHeartAdCooldown() ? "bg-slate-200 text-slate-400" : "bg-[#f43f5e] text-white"
                  )}
                >
                  {isProcessing === 'ad' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    getHeartAdCooldown() || (user.isPremium ? t('shop.heart_ticket.claim') : t('shop.heart_ticket.watch_ad'))
                  )}
                </button>

              </div>



              {/* Heart tickets for star ticket */}
              <div className="bg-white p-5 rounded-[32px] border-2 border-slate-100 flex items-center gap-5 shadow-sm">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center shrink-0 border-2 border-blue-100">
                  <CurrencyIcon type="ticket_star" size="xl" />
                </div>
                <div className="flex-1">
                  <div className="font-black text-slate-900 uppercase whitespace-nowrap">{t('shop.star_ticket.name')}</div>
                  <div className="text-[10px] text-blue-500 font-black uppercase tracking-widest mt-0.5">{t('shop.star_ticket.owned', { count: user.starTickets })}</div>
                </div>
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    handleBuyStarTicket();
                  }}
                  disabled={isProcessing === 'star_ticket'}
                  className="bg-white text-slate-900 w-[120px] py-3 rounded-2xl font-num text-lg uppercase toy-3d-button border-none shrink-0 flex items-center justify-center gap-2"
                >
                  {isProcessing === 'star_ticket' ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>
                      <CurrencyIcon type="ticket_heart" size="sm" />
                      <span className="font-num font-black text-slate-900">20</span>
                    </>
                  )}
                </button>
              </div>

              {/* Gems for refill */}
              <div className="bg-white p-5 rounded-[32px] border-2 border-slate-100 flex items-center gap-5 shadow-sm">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center shrink-0 border-2 border-red-100">
                  <Heart className="w-8 h-8 text-red-500 fill-red-500" />
                </div>
                <div className="flex-1">
                  <div className="font-black text-slate-900 uppercase whitespace-nowrap">{t('shop.max_refill.name')}</div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{t('shop.max_refill.desc')}</div>
                </div>
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    handleRefillHearts();
                  }}
                  disabled={isProcessing === 'refill' || user.hearts >= 5}
                  className={cn(
                    "w-[120px] py-3 rounded-2xl font-num text-lg uppercase toy-3d-button border-none shrink-0 flex items-center justify-center gap-2",
                    user.hearts >= 5 ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-white text-slate-900"
                  )}
                >
                  {isProcessing === 'refill' ? <Loader2 className="w-5 h-5 animate-spin" /> : user.hearts >= 5 ? t('shop.max_refill.full') : (
                    <>
                      <CurrencyIcon type="gem" size="sm" />
                      <span className="font-num font-black text-slate-900">25</span>
                    </>
                  )}
                </button>
              </div>

              <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2 mt-6 px-2">{t('shop.sections.reward_boxes')}</h3>
              
              <div className="grid grid-cols-1 gap-4">
                {[
                  { id: 'bronze', tier: 'Bronze', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-100', desc: t('shop.boxes.bronze_desc') },
                  { id: 'silver', tier: 'Silver', color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', desc: t('shop.boxes.silver_desc') },
                  { id: 'gold',   tier: 'Gold',   color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-100', desc: t('shop.boxes.gold_desc') },
                  { id: 'diamond', tier: 'Diamond', color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-100', desc: t('shop.boxes.diamond_desc'), premium: true }
                ].map(b => {
                  const owned = user.rewardBoxes[b.id as keyof UserData['rewardBoxes']] || 0;
                  const costs = { bronze: '1 AD', silver: '10K Coins', gold: '20 Hoshi', diamond: '50 Hoshi' }[b.id];
                  
                  return (
                    <div key={b.id} className="bg-white p-5 rounded-[32px] border-2 border-slate-100 flex flex-col gap-4 shadow-sm relative overflow-hidden group">
                      {b.premium && (
                        <div className="absolute top-0 right-0 bg-cyan-500 text-white text-[8px] font-black px-3 py-1 rounded-bl-xl uppercase tracking-widest z-10 transition-transform group-hover:-translate-y-1">
                          {t('shop.boxes.top_tier')}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-5">
                        <div className="w-16 h-16 flex items-center justify-center shrink-0">
                          <ChestIcon tier={b.id as any} size="md" />
                        </div>
                        <div className="flex-1">
                          <div className="font-black text-slate-900 uppercase leading-tight">{t('tiers.' + b.id)} Box</div>
                          {b.id === 'bronze' && (
                            <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{t('shop.boxes.limit_bronze')}</div>
                          )}
                        </div>
                        <button 
                          onClick={() => {
                            audioEngine.playSound('click');
                            setShowDropRates(b.id as any);
                          }}
                          className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-2xl transition-all"
                        >
                          <HelpCircle className="w-5 h-5" />
                        </button>

                      </div>

                      <div className="flex items-center gap-3 pt-1">
                        <button 
                          onClick={() => {
                            audioEngine.playSound('click');
                            onOpenBox(b.id as any);
                          }}
                          disabled={owned === 0}
                          className={cn(
                            "flex-1 py-3 rounded-2xl font-num text-base uppercase transition-all shadow-sm active:scale-95 disabled:opacity-20 disabled:active:scale-100",
                            owned > 0 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"
                          )}
                        >

                          {owned > 0 ? t('shop.boxes.open_with_count', { count: owned }) : t('shop.boxes.open')}
                        </button>
                        <button 
                          id={b.id === 'bronze' ? "shop-buy-bronze-box" : undefined}
                          onClick={() => {
                            audioEngine.playSound('click');
                            const tierId = b.id;
                            
                            // Pre-check for Gems/Coins
                            if (tierId === 'silver' && user.coins < 10000) {
                              showAlert(t('shop.alerts.not_enough_coins'), t('shop.alerts.not_enough_coins_box'), "error");
                              return;
                            }

                            if (tierId === 'gold' && user.gems < 20) {
                              showNotEnoughGemsAlert();
                              return;
                            }
                            if (tierId === 'diamond' && user.gems < 50) {
                              showNotEnoughGemsAlert();
                              return;
                            }

                            setBuyingBox(b.tier as any);
                          }}
                          disabled={b.id === 'bronze' && !!getBoxAdCooldown()}
                          className={cn(
                            "w-[120px] py-3 rounded-2xl font-num text-lg uppercase shrink-0 flex items-center justify-center gap-2 toy-3d-button border-none shrink-0 transition-all",
                            b.id === 'bronze' ? (!!getBoxAdCooldown() ? "bg-slate-100 text-slate-300" : "bg-[#f43f5e] text-white") : "bg-white text-slate-900"
                          )}
                        >
                          {b.id === 'bronze' ? (
                            isProcessing === 'bronze' ? <Loader2 className="w-5 h-4 animate-spin" /> : (getBoxAdCooldown() || (user.isPremium ? t('shop.boxes.free') : t('shop.boxes.watch_ad')))
                          ) : b.id === 'silver' ? (
                            <>
                              <CurrencyIcon type="coin" size="sm" />
                              <span className="font-num font-black text-slate-900">10,000</span>
                            </>
                          ) : b.id === 'gold' ? (
                            <>
                              <CurrencyIcon type="gem" size="sm" />
                              <span className="font-num font-black text-slate-900">20</span>
                            </>
                          ) : (
                            <>
                              <CurrencyIcon type="gem" size="sm" />
                              <span className="font-num font-black text-slate-900">50</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ) : activeTab === 'avatar' ? (
            <motion.div 
              key="avatar"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-1 gap-4"
            >
              {selectedCategory === null ? (
                <div className="space-y-4">
                  <div className="px-2">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">{t('shop.avatar.title')}</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">{t('shop.avatar.subtitle')}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Abstract Group Card */}
                    {(() => {
                      const abstractAvatars = AVATARS.filter(a => a.category === 'Abstract' && a.price && !user.purchasedAvatars?.includes(a.id));
                      const totalAbstract = abstractAvatars.length;
                      
                      return (
                        <button
                          onClick={() => {
                            audioEngine.playSound('click');
                            setSelectedCategory('Abstract');
                          }}
                          className="bg-white p-6 rounded-[32px] border-2 border-slate-100 flex items-center justify-between shadow-sm hover:shadow-md transition-all hover:scale-[1.01] active:scale-[0.99] text-left group"
                        >
                          <div className="flex items-center gap-5">
                            <div className="w-16 h-16 bg-slate-950 rounded-2xl flex items-center justify-center shrink-0 border-2 border-slate-900 overflow-hidden shadow-inner">
                              {abstractAvatars.length > 0 ? (
                                <GalleryPhoto id={abstractAvatars[0].id} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-black" />
                              )}
                            </div>
                            <div>
                              <div className="font-black text-slate-950 uppercase text-lg tracking-tight">{t('shop.avatar.abstract')}</div>
                              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                {totalAbstract > 0 ? t('shop.avatar.available', { count: totalAbstract }) : t('shop.avatar.all_collected')}
                              </div>
                            </div>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-2xl text-slate-400 group-hover:text-slate-950 group-hover:bg-slate-100 transition-colors">
                            <ChevronRight className="w-5 h-5" />
                          </div>
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Category Details View */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        audioEngine.playSound('click');
                        setSelectedCategory(null);
                      }}
                      className="p-3 bg-white rounded-2xl toy-3d-button border-none"
                    >
                      <ArrowLeft className="w-4 h-4 text-slate-600" />
                    </button>
                    <div>
                      <h3 className="text-xl font-num font-black uppercase text-slate-950">{t('shop.avatar.category_gallery', { category: selectedCategory })}</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{t('shop.avatar.category_subtitle')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 pt-2">
                    {(() => {
                      const list = AVATARS.filter(a => a.category === selectedCategory && a.price && !user.purchasedAvatars?.includes(a.id));
                      if (list.length === 0) {
                        return (
                          <div className="text-center py-12 bg-white/50 rounded-[32px] border border-slate-100">
                            <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">{t('shop.avatar.all_purchased')}</p>
                          </div>
                        );
                      }
                      return list.map((avatar) => (
                        <motion.div
                          layout
                          key={avatar.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="bg-white p-4 rounded-[32px] flex items-center gap-4 shadow-sm border-b-4 border-slate-200 group relative overflow-hidden"
                        >
                          <GalleryPhoto id={avatar.id} size="lg" className="shrink-0" />
                          
                          <div className="flex-1">
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest opacity-60 mb-0.5">{t('shop.avatar.theme', { category: avatar.category })}</div>
                            <div className="text-lg font-num text-slate-900 uppercase leading-tight">{avatar.name}</div>
                          </div>

                          <button
                            onClick={() => handleBuyAvatar(avatar.id)}
                            disabled={isProcessing === avatar.id}
                            className="bg-slate-50 px-4 py-3 rounded-2xl flex items-center gap-2 toy-3d-button border-none min-w-[100px] justify-center transition-all hover:bg-white active:bg-slate-100"
                          >
                            {isProcessing === avatar.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                            ) : (
                              <>
                                <span className="text-sm font-num font-black text-slate-900">{avatar.price?.toLocaleString()}</span>
                                <CurrencyIcon type="coin" size="sm" />
                              </>
                            )}
                          </button>
                        </motion.div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </motion.div>
          ) : activeTab === 'bank' ? (
            <motion.div 
              key="bank"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-2 gap-4"
            >
              {/* Hoshi Bank header removed */}
               {gemPackages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={cn(
                    "bg-white p-6 rounded-[40px] border-2 border-slate-100 flex flex-col items-center gap-4 shadow-sm relative overflow-hidden transition-all hover:shadow-md group",
                    pkg.id === 'gems_galactic' && "border-rose-100 shadow-rose-100/50"
                  )}
                >
                  {pkg.id === 'gems_galactic' && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Asymmetrical Star Scatter */}
                      <CurrencyIcon type="gem" size="sm" className="absolute top-2 left-8 opacity-40 rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute top-10 left-1 opacity-20 -rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute -top-1 left-20 opacity-30 rotate-45" />
                      
                      <CurrencyIcon type="gem" size="sm" className="absolute top-1 right-10 opacity-40 -rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute top-14 -right-1 opacity-20 rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute -top-2 right-4 opacity-30 -rotate-45" />

                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-12 -left-2 opacity-20 rotate-45" />
                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-2 left-14 opacity-40 -rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-0 left-4 opacity-30 rotate-12" />

                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-16 -right-2 opacity-20 -rotate-12" />
                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-4 right-12 opacity-40 rotate-45" />
                      <CurrencyIcon type="gem" size="sm" className="absolute bottom-0 right-3 opacity-30 -rotate-12" />
                      
                      <div className="absolute inset-0 bg-cyan-400/5 blur-2xl rounded-[40px] -z-10" />
                    </div>
                  )}
                  {/* Best Value badge removed */}

                  <div className={cn("w-24 h-24 rounded-3xl flex items-center justify-center relative", pkg.color)}>
                    {/* Dynamic Gem Stack */}
                    <div className="relative w-full h-full flex items-center justify-center">
                       {pkg.gems === 1 ? (
                         <CurrencyIcon type="gem" size="lg" className="scale-125" />
                       ) : pkg.id === 'gems_hoard' ? (
                         <div className="relative w-full h-full flex items-center justify-center">
                           {/* 3-layer stack for Hoard */}
                           {[...Array(4)].map((_, i) => (
                             <div 
                               key={`back-${i}`} 
                               className="absolute transition-transform group-hover:scale-110"
                               style={{ 
                                 transform: `translate(${(i - 1.5) * 12}px, -15px)`,
                                 zIndex: 50
                                }}
                             >
                               <CurrencyIcon type="gem" size="md" />
                             </div>
                           ))}
                           {[...Array(5)].map((_, i) => (
                             <div 
                               key={`mid-${i}`} 
                               className="absolute transition-transform group-hover:scale-110"
                               style={{ 
                                 transform: `translate(${(i - 2) * 12}px, 0px)`,
                                 zIndex: 60
                               }}
                             >
                               <CurrencyIcon type="gem" size="md" />
                             </div>
                           ))}
                           {[...Array(6)].map((_, i) => (
                             <div 
                               key={`front-${i}`} 
                               className="absolute transition-transform group-hover:scale-110"
                               style={{ 
                                 transform: `translate(${(i - 2.5) * 12}px, 15px)`,
                                 zIndex: 70
                               }}
                             >
                               <CurrencyIcon type="gem" size="md" />
                             </div>
                           ))}
                         </div>
                       ) : (
                         <div className="relative w-full h-full flex items-center justify-center">
                           {[...Array(pkg.gems)].map((_, i) => (
                             <div 
                               key={i} 
                               className="absolute transition-transform group-hover:scale-110"
                               style={{ 
                                 transform: `translate(${(i - (pkg.gems-1)/2) * (pkg.gems > 15 ? 8 : 10)}px, ${(i % 2 === 0 ? -6 : 6)}px) scale(${1 - Math.abs(i - (pkg.gems-1)/2) * 0.05})`,
                                 zIndex: 100 - Math.abs(i - (pkg.gems-1)/2)
                               }}
                             >
                               <CurrencyIcon type="gem" size={pkg.gems > 10 ? 'md' : 'lg'} />
                             </div>
                           ))}
                         </div>
                       )}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-[10px] font-black text-purple-400 uppercase tracking-widest leading-none mb-1">{pkg.tier}</div>
                    <div className="text-3xl font-num font-black text-slate-900 leading-none">{pkg.amount.toLocaleString()}</div>
                    {/* Label removed */}
                    <div className={cn(
                      "text-[9px] font-black uppercase tracking-widest mt-2 px-3 py-1 rounded-full",
                      pkg.bonus === t('shop.gem_packages.base_rate') ? "text-slate-300" : "text-emerald-500 bg-emerald-50"
                    )}>
                      {pkg.bonus}
                    </div>
                  </div>

                  <button 
                     onClick={() => {
                      audioEngine.playSound('click');
                      if (purchaseManager.isAndroid()) {
                        purchaseManager.buyGems(pkg.id);
                      } else {
                        showAlert(t('shop.alerts.purchase_redirect'), t('shop.alerts.purchase_redirect_msg'), "info");
                      }
                    }}
                    className="w-full py-3.5 rounded-2xl font-num text-xl uppercase toy-3d-button border-none transition-all bg-slate-900 text-white"
                  >
                    {pkg.price}
                  </button>

                </div>
              ))}


            </motion.div>
          ) : null}

          {activeTab === 'special' && (
            <motion.div
              key="special"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-1 gap-4 text-slate-900"
            >
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-2">{t('shop.sections.pro_subscription')}</h3>
              
              <button
                onClick={() => {
                  audioEngine.playSound('click');
                  setShowPremiumModal(true);
                }}
                className={cn(
                  "w-full p-4 rounded-3xl border-none shadow-lg flex items-center justify-between group active:scale-95 transition-all overflow-hidden relative cursor-pointer",
                  user.isPremium
                    ? "bg-gradient-to-r from-purple-500 to-purple-600"
                    : "bg-gradient-to-r from-amber-400 to-amber-500"
                )}
              >
                <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
                <div className="flex items-center gap-3 relative z-10">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Crown className="w-5 h-5 text-white fill-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-white font-black text-sm uppercase tracking-tight">
                      {user.isPremium ? t('shop.special.pro_member') : t('shop.special.upgrade_to_pro')}
                    </div>
                    <div className={cn(
                      "text-[8px] font-black uppercase tracking-widest leading-none mt-0.5",
                      user.isPremium ? "text-purple-200" : "text-amber-900/50"
                    )}>
                      {t('shop.special.pro_benefits')}
                    </div>
                  </div>
                </div>
                <Star className="w-5 h-5 text-white relative z-10" />
              </button>

              {(() => {
                const activeOffer = MILESTONE_OFFERS.find(o => o.id === user.activeMilestoneOffer?.id);
                if (!activeOffer || !offerTimeLeft) return null;
                return (
                  <div className="mt-6">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4 px-2">{t('shop.sections.milestone_offer')}</h3>
                    <div className="relative overflow-hidden bg-gradient-to-br from-amber-600 to-amber-900 border-2 border-amber-400 rounded-[28px] p-4 shadow-xl text-white group flex flex-col gap-3">
                      <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:250px_250px] animate-[pulse_3s_infinite]" />
                      
                      <div className="flex justify-end items-center relative z-10">
                        <div className="flex items-center gap-1 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
                          <Loader2 className="w-3.5 h-3.5 animate-[spin_10s_linear_infinite] text-amber-400" />
                          <span className="font-num font-bold text-xs tracking-wider text-amber-300">{offerTimeLeft}</span>
                        </div>
                      </div>

                      <div className="relative z-10 mt-0.5">
                        <h4 className="text-2xl font-num leading-tight font-black uppercase text-amber-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                          {activeOffer.name}
                        </h4>
                      </div>

                      <div className="bg-black/30 backdrop-blur-md border border-white/5 rounded-2xl p-3 flex flex-col gap-2 relative z-10">
                        <div className="flex items-center justify-between select-none">
                          <span className="text-[9px] font-black tracking-widest text-amber-400 uppercase">{t('shop.special.unlocked_items')}</span>
                          <button
                            onClick={() => {
                              audioEngine.playSound('click');
                              showAlert(
                                activeOffer.name,
                                `${t('shop.special.unlocked_items')}\n\n` +
                                `• ${activeOffer.rewards.prisms.count}x 3-Star Anima Ticket\n` +
                                `• ${activeOffer.rewards.boxes.diamond}x Diamond Box\n` +
                                (activeOffer.rewards.starTickets > 0 ? `• ${activeOffer.rewards.starTickets}x Star Ticket\n` : '') +
                                `• ${activeOffer.rewards.coins.toLocaleString()} Coins\n` +
                                `• ${activeOffer.rewards.gems.toLocaleString()} Hoshi gems`,
                                'info'
                              );
                            }}
                            className="p-1 -mr-1 rounded-lg hover:bg-white/10 text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
                            aria-label="View bundle reward details"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <div className="flex items-center gap-1.5 bg-blue-500/20 border border-blue-400/30 px-2 py-1 rounded-xl shadow-sm">
                            <CurrencyIcon type="prism" size="sm" stars={3} color="blue" />
                            <span className="font-num text-xs font-black text-blue-300">{activeOffer.rewards.prisms.count}x</span>
                          </div>
                          <div className="flex items-center gap-1.5 bg-cyan-500/20 border border-cyan-400/30 px-2 py-1 rounded-xl shadow-sm">
                            <ChestIcon tier="diamond" size="xs" className="!w-4 !h-[14px]" />
                            <span className="font-num text-xs font-black text-cyan-300">{activeOffer.rewards.boxes.diamond}x</span>
                          </div>
                          {activeOffer.rewards.starTickets > 0 && (
                            <div className="flex items-center gap-1.5 bg-yellow-500/20 border border-yellow-400/30 px-2 py-1 rounded-xl shadow-sm">
                              <CurrencyIcon type="ticket_star" size="sm" />
                              <span className="font-num text-xs font-black text-yellow-300">{activeOffer.rewards.starTickets}x</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 bg-yellow-400/20 border border-yellow-400/30 px-2 py-1 rounded-xl shadow-sm">
                            <CurrencyIcon type="coin" size="sm" />
                            <span className="font-num text-xs font-black text-yellow-200">{activeOffer.rewards.coins.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-1.5 bg-purple-500/20 border border-purple-400/30 px-2 py-1 rounded-xl shadow-sm">
                            <CurrencyIcon type="gem" size="sm" />
                            <span className="font-num text-xs font-black text-purple-300">{activeOffer.rewards.gems.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="relative z-10 mt-1 w-full">
                        <button
                          onClick={() => {
                            audioEngine.playSound('click');
                            handleBuyMilestoneOffer(activeOffer);
                          }}
                          disabled={isProcessing === activeOffer.id}
                          className="w-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-num font-black text-xl py-3 rounded-2xl border-none shadow-lg transition-all active:scale-95 duration-150 uppercase flex items-center justify-center"
                        >
                          {isProcessing === activeOffer.id ? (
                            <Loader2 className="w-6 h-6 animate-spin" />
                          ) : (
                            activeOffer.price
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {purchaseManager.isAndroid() && (
                <div className="col-span-1 mt-4 flex justify-center">
                  <button 
                    onClick={() => {
                      audioEngine.playSound('click');
                      purchaseManager.restorePurchases();
                      showAlert(t('shop.alerts.restore'), t('shop.alerts.restore_msg'), "info");
                    }}
                    className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] hover:text-slate-600 transition-colors py-4"
                  >
                    {t('shop.special.restore_purchases')}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <GameModal 
        isOpen={modal.open}
        onClose={() => setModal({ ...modal, open: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        confirmLabel={modal.confirmLabel}
        cancelLabel={modal.cancelLabel}
      />

      <AnimatePresence>
        {showDropRates && (
          <div className="absolute inset-0 z-[600] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" 
              onClick={() => setShowDropRates(null)} 
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-white rounded-[40px] w-full max-w-sm overflow-hidden relative shadow-2xl"
            >
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                    <div>
                      <h3 className="text-2xl font-num uppercase tracking-tight text-slate-900 leading-none">{t('tiers.' + showDropRates).toUpperCase()} BOX</h3>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{t('shop.boxes.probability_table')}</p>
                    </div>
                </div>
                <div className="p-8 space-y-1">
                    {(DROP_RATES as any)[showDropRates].map((rate: any, i: number) => (
                        <div key={i} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 px-2 rounded-xl transition-colors">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{rate.label}</span>
                            <span className="text-sm font-num font-black text-slate-900">{rate.chance}</span>
                        </div>
                    ))}
                </div>
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    setShowDropRates(null);
                  }}
                  className="w-full py-6 bg-slate-900 text-white font-num text-xl uppercase tracking-widest hover:bg-slate-800 transition-colors"
                >
                  {t('shop.boxes.got_it')}
                </button>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {buyingBox && (
        <QuantityPickerModal
          user={user}
          tier={buyingBox}
          onClose={() => setBuyingBox(null)}
          isOpening={false}
          purchaseOnly={true}
          onConfirm={handleConfirmQuantity}
        />
      )}

      {/* Dev Checkout Simulator Modal */}
      <AnimatePresence>
        {simulatingOffer && (
          <div className="absolute inset-0 z-[700] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" 
              onClick={() => setSimulatingOffer(null)} 
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-slate-900 border-2 border-amber-400/50 rounded-[40px] w-full max-w-sm overflow-hidden relative shadow-2xl z-10 p-8 text-white text-center"
            >
              <Award className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-[bounce_2s_infinite]" />
              
              <h3 className="text-2xl font-num text-amber-300 uppercase tracking-tight leading-none mb-2">{t('shop.iap_simulator.title')}</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">{t('shop.iap_simulator.subtitle')}</p>
              
              <div className="bg-slate-950/50 border border-white/5 rounded-2xl p-5 mb-8 text-left space-y-3">
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('shop.iap_simulator.product_id')}</span>
                  <span className="font-num text-xs font-bold text-amber-300">{simulatingOffer.id}</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('shop.iap_simulator.bundle_title')}</span>
                  <span className="text-xs font-bold uppercase text-white">{simulatingOffer.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('shop.iap_simulator.price_mock')}</span>
                  <span className="font-num text-sm font-black text-emerald-400">{simulatingOffer.price}</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    handleCompleteSimulatedPurchase();
                  }}
                  disabled={isProcessing === simulatingOffer.id}
                  className="w-full py-4 bg-emerald-500 text-white font-num text-lg uppercase tracking-wider rounded-2xl hover:bg-emerald-400 transition-colors shadow-lg active:scale-95 disabled:opacity-50"
                >
                  {isProcessing === simulatingOffer.id ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t('shop.iap_simulator.complete')}
                </button>
                
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    setSimulatingOffer(null);
                  }}
                  disabled={isProcessing === simulatingOffer.id}
                  className="w-full py-4 bg-slate-800 text-slate-400 font-num text-lg uppercase tracking-wider rounded-2xl hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                >
                  {t('shop.iap_simulator.cancel')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPremiumModal && (
          <PremiumModal 
            user={user}
            onClose={() => setShowPremiumModal(false)}
            onSetPremiumStatus={onSetPremiumStatus}
          />
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}
